import { ExpressionModel } from '../Models/ExpressionModel';
import { JsonSchemaModel } from '../Models/JsonSchemaModel';
import { IVariableScope } from '../Models/VariableScopes/IVariableScope';
import { VariableScope } from '../Models/VariableScopes/VariableScope';
import { convertJsonSchemaPropertyToEntityProperty, getJsonSchemaFromProperty } from './EntityMetadataModelConverter';
import { schemaMatchesValue } from './JsonSchemaTypes';

/** Only guards with known, side-effect-free operands contribute refinements. */
export function narrowSchema(schema: JsonSchemaModel, path: string[], value: unknown, equal: boolean): JsonSchemaModel | undefined {
	const variants = schema.oneOf ?? schema.anyOf;
	if (variants) {
		const branches = variants.map((branch) => narrowSchema(branch, path, value, equal)).filter((branch): branch is JsonSchemaModel => !!branch);
		if (!branches.length) return undefined;
		return { ...schema, oneOf: schema.oneOf ? branches : undefined, anyOf: schema.oneOf ? undefined : branches };
	}
	if (Array.isArray(schema.type)) {
		const branches = schema.type.map((type) => narrowSchema({ ...schema, type }, path, value, equal)).filter((branch): branch is JsonSchemaModel => !!branch);
		return branches.length ? { anyOf: branches } : undefined;
	}
	if (path.length) {
		const member = schema.properties?.[path[0]];
		if (!member) return schema;
		const narrowed = narrowSchema(member, path.slice(1), value, equal);
		if (!narrowed && !equal && !schema.required?.includes(path[0])) return schema;
		return narrowed ? { ...schema, properties: { ...schema.properties, [path[0]]: narrowed } } : undefined;
	}
	if (equal) return schemaMatchesValue(schema, value) ? { ...schema, const: value } : undefined;
	if (('const' in schema && schemaMatchesValue({ const: schema.const }, value)) || (value === null && schema.type === 'null')) return undefined;
	return schema;
}

export function isScopeGuard(expression: ExpressionModel | undefined): boolean {
	if (!expression) return false;
	if (expression.Type === 'Not') return isScopeGuard(expression.Value);
	if (expression.Type === 'IsNull') return expression.Value?.Type === 'Var' && !expression.Value.Target;
	return ['Eq', 'NotEq'].includes(expression.Type) && expression.Value?.length === 2 &&
		(expression.Value as ExpressionModel[]).every((operand) => operand.Type === 'Const' || (operand.Type === 'Var' && !operand.Target));
}

export function narrowVariableScope(scope: IVariableScope, condition: ExpressionModel | undefined, truth = true): VariableScope {
	const result = new VariableScope(new Map(scope.getVariables()));
	for (const message of scope.getDiagnostics?.() ?? []) result.addDiagnostic(message);
	if (condition?.Type === 'Not') return narrowVariableScope(result, condition.Value, !truth);
	if ((condition?.Type === 'And' && truth) || (condition?.Type === 'Or' && !truth)) {
		return (condition.Value as ExpressionModel[]).reduce((current, operand) => narrowVariableScope(current, operand, truth), result);
	}
	const refine = (variable: ExpressionModel, value: unknown, equal: boolean) => {
		if (variable.Target || !Array.isArray(variable.Value) || !variable.Value.length) return;
		const [name, ...path] = variable.Value as string[];
		const original = result.getVariable(name);
		if (!original) return;
		const schema = narrowSchema(getJsonSchemaFromProperty(original), path, value, equal);
		const definite = !!original.isRequired || (equal && value !== null && value !== undefined) || (!equal && value === null);
		if (schema) result.setVariable(name, {
			...convertJsonSchemaPropertyToEntityProperty(name, schema, definite),
			scopeOrigins: original.scopeOrigins,
			possiblyUnavailable: !definite && original.possiblyUnavailable,
		});
	};
	if (condition?.Type === 'IsNull' && condition.Value?.Type === 'Var') refine(condition.Value, null, truth);
	if ((condition?.Type === 'Eq' || condition?.Type === 'NotEq') && condition.Value?.length === 2) {
		const [left, right] = condition.Value as ExpressionModel[];
		const equal = condition.Type === 'Eq' ? truth : !truth;
		if (left.Type === 'Var' && right.Type === 'Const') refine(left, right.Value, equal);
		if (right.Type === 'Var' && left.Type === 'Const') refine(right, left.Value, equal);
	}
	return result;
}
